import {Injectable} from '@angular/core';

/**
 * Result returned by warnAndClearTax() — the service clears the detail fields
 * and returns the expired tax info so the component can show the UI warning.
 */
export interface TaxClearResult {
  cleared: boolean;
  taxCode: string;
  taxName: string;
}

/**
 * Result returned by applyTaxToDetail() — contains the computed tax amounts
 * and any expired-tax warnings to surface in the UI.
 */
export interface TaxApplicationResult {
  taxAmount1: number | null;
  taxAmount2: number | null;
  warnings: TaxClearResult[];
}

/**
 * TransactionTaxService
 *
 * Responsible for all tax lookup, rate calculation, expiry/zero-rate detection,
 * and tax-slot management across all transaction types (AR Invoice, AP Invoice,
 * Sales Order, Purchase Order, Quote, Shipment).
 *
 * Design principles:
 * - All methods accept data as parameters; no component state is read directly.
 * - UI side-effects (toast notifications) are NOT performed here. Methods return
 *   TaxClearResult objects so the component can decide how to notify the user.
 * - Tax list and tax rate list are passed in as parameters, matching the data
 *   currently loaded into vm.tax and vm.taxRate by the form's fetchLookupData().
 *   When the /api/tax/active-with-rates endpoint is fully wired into the form
 *   (Phase 11 cleanup), these parameters will be replaced by a single typed list.
 *
 * Phase 2 of the Invoice Form Refactor — see docs/Invoice Form Refactor.md
 */
@Injectable({
  providedIn: 'root'
})
export class TransactionTaxService {

  // ---------------------------------------------------------------------------
  // Tax lookup helpers
  // ---------------------------------------------------------------------------

  /**
   * Find a tax object by its id from the loaded tax list.
   * Returns undefined if not found.
   *
   * Replaces: invoice-form.getTaxById()
   */
  getTaxById(id: any, taxList: any[]): any {
    try {
      return (taxList || []).find((t: any) => t && t.id === id);
    } catch {
      return undefined;
    }
  }

  /**
   * Look up the current rate for a tax id from the loaded tax rate list.
   * Returns 0 if no matching rate record is found.
   *
   * Replaces: invoice-form.getTaxRate()
   *
   * @param taxId     The tax id to look up
   * @param taxRates  The vm.taxRate array (objects with tax_id and rate fields)
   */
  getTaxRate(taxId: string | number, taxRates: any[]): number {
    interface TaxRateModel {
      tax_id: string | number;
      rate: number;
    }

    const entry = Object.values(taxRates || []).find(
      (r: TaxRateModel) => r.tax_id == taxId
    ) as TaxRateModel | undefined;
    return entry ? entry.rate : 0;
  }

  /**
   * Calculate the tax amount for a single line.
   * result = rate% × amount × quantity, rounded to 2 decimal places.
   *
   * Replaces: invoice-form.calculateTaxAmount()
   */
  calculateTaxAmount(rate: number, amount: number, quantity: number): number {
    return Math.round(rate * amount * quantity * 100) / 100;
  }

  // ---------------------------------------------------------------------------
  // Expiry / usability checks
  // ---------------------------------------------------------------------------

  /**
   * Determine whether a tax object is expired or inactive.
   * Returns true (expired) if the tax is null, has a past end_date,
   * or is flagged expired/inactive.
   *
   * Replaces: invoice-form.isTaxExpired()
   */
  isTaxExpired(tax: any): boolean {
    if (!tax) {
      return true;
    }
    try {
      const today = new Date();
      if (tax.end_date) {
        const end = new Date(tax.end_date);
        if (isFinite(end.getTime()) && end < today) {
          return true;
        }
      }
      if (tax.expired === true || tax.inactive === true) {
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  /**
   * Determine whether a tax id resolves to a zero rate.
   * A zero or non-finite rate is treated as unusable.
   *
   * Replaces: invoice-form.isZeroRateTax()
   */
  isZeroRateTax(taxId: any, taxRates: any[]): boolean {
    const rate = Number(this.getTaxRate(taxId, taxRates));
    return !isFinite(rate) || rate === 0;
  }

  /**
   * Final usability gate: a tax is usable only if it exists in the tax list,
   * is not expired/inactive, and has a non-zero rate.
   *
   * Replaces: invoice-form.isTaxUsable()
   */
  isTaxUsable(taxId: any, taxList: any[], taxRates: any[]): boolean {
    const tax = this.getTaxById(taxId, taxList);
    if (!tax) {
      return false;
    }
    if (this.isTaxExpired(tax)) {
      return false;
    }
    if (this.isZeroRateTax(taxId, taxRates)) {
      return false;
    }
    return true;
  }

  // ---------------------------------------------------------------------------
  // Detail-line tax slot management
  // ---------------------------------------------------------------------------

  /**
   * Clear the specified tax slot (1 or 2) on a detail line and return the
   * expired tax info so the component can show a warning toast.
   *
   * The component is responsible for calling the notification service with
   * the returned taxCode and taxName.
   *
   * Replaces: invoice-form.warnAndClearTax()
   */
  warnAndClearTax(detail: any, slot: 1 | 2, tax: any): TaxClearResult {
    const idField = slot === 1 ? 'tax_id_1' : 'tax_id_2';
    const amtField = slot === 1 ? 'tax_amount_1' : 'tax_amount_2';
    const codeField = slot === 1 ? 'taxCode_1' : 'taxCode_2';

    detail[idField] = null;
    detail[amtField] = null;
    if (Object.prototype.hasOwnProperty.call(detail, codeField)) {
      detail[codeField] = null;
    }

    return {
      cleared: true,
      taxCode: tax?.code ?? '',
      taxName: tax?.name ?? ''
    };
  }

  /**
   * Clear tax references on a detail line when the line amount is zero
   * or when a referenced tax has a zero rate.
   *
   * Replaces: invoice-form.clearZeroTaxRefs()
   */
  clearZeroTaxRefs(detail: any, taxRates: any[]): void {
    try {
      const amt = Number(detail?.amount || 0);

      const clearSlot = (n: 1 | 2) => {
        const idField = `tax_id_${n}` as const;
        const amtField = `tax_amount_${n}` as const;
        const codeField = (`taxCode_${n}` as unknown) as 'taxCode_1' | 'taxCode_2';
        detail[idField] = null;
        detail[amtField] = 0;
        if (Object.prototype.hasOwnProperty.call(detail, codeField)) {
          detail[codeField] = null;
        }
      };

      if (amt === 0) {
        clearSlot(1);
        clearSlot(2);
        return;
      }

      for (const n of [1, 2] as const) {
        const idField = `tax_id_${n}` as const;
        const taxId = detail?.[idField];
        if (taxId != null && this.getTaxRate(taxId, taxRates) === 0) {
          clearSlot(n);
        }
      }
    } catch {
      // no-op — be resilient to malformed detail objects
    }
  }

  /**
   * Calculate the tax amount for a single tax slot on a detail line.
   * Sets the taxCode_1 / taxCode_2 helper field on the detail item.
   * Returns 0 and clears the slot if the tax rate is zero (expired/unusable).
   * Returns null if no tax id is set for the slot.
   *
   * When a zero-rate tax is detected, the expired tax info is included in the
   * returned result so the component can show a warning toast.
   *
   * Replaces: invoice-form.calculateSingleTaxAmount()
   *
   * @param detailItem  The detail line object (mutated in place for taxCode fields)
   * @param taxIdField  Which tax slot to process ('tax_id_1' | 'tax_id_2')
   * @param taxList     The vm.tax array
   * @param taxRates    The vm.taxRate array
   */
  calculateSingleTaxAmount(
    detailItem: any,
    taxIdField: 'tax_id_1' | 'tax_id_2',
    taxList: any[],
    taxRates: any[]
  ): { amount: number; warning: TaxClearResult | null } {
    const taxId = detailItem[taxIdField];
    if (!taxId) {
      return {amount: 0, warning: null};
    }

    // Set the taxCode helper field for display
    if (taxIdField === 'tax_id_1') {
      detailItem.taxCode_1 = (taxList || []).filter(t => t.id === taxId);
    } else {
      detailItem.taxCode_2 = (taxList || []).filter(t => t.id === taxId);
    }

    const taxRate = this.getTaxRate(taxId, taxRates);

    if (taxRate === 0) {
      // Zero-rate: clear the slot and return warning info for the component to display
      const badTax = (taxList || []).find(t => t.id === taxId);
      const amtField = taxIdField === 'tax_id_1' ? 'tax_amount_1' : 'tax_amount_2';
      const codeField = taxIdField === 'tax_id_1' ? 'taxCode_1' : 'taxCode_2';

      detailItem[taxIdField] = null;
      detailItem[amtField] = 0;
      if (Object.prototype.hasOwnProperty.call(detailItem, codeField)) {
        detailItem[codeField] = null;
      }

      return {
        amount: 0,
        warning: {
          cleared: true,
          taxCode: badTax?.code ?? '',
          taxName: badTax?.name ?? ''
        }
      };
    }

    return {
      amount: this.calculateTaxAmount(taxRate, detailItem.amount, detailItem.quantity),
      warning: null
    };
  }

  /**
   * Calculate and apply tax amounts for both tax slots on a detail line.
   * Returns the computed amounts and any warnings for the component to act on.
   *
   * Replaces: invoice-form.calculateTaxValues() (the pure calculation part)
   *
   * @param detail    The detail line object (mutated in place)
   * @param taxList   The vm.tax array
   * @param taxRates  The vm.taxRate array
   */
  applyTaxToDetail(
    detail: any,
    taxList: any[],
    taxRates: any[]
  ): TaxApplicationResult {
    const warnings: TaxClearResult[] = [];

    if (Number(detail?.amount || 0) === 0) {
      detail.tax_amount_1 = null;
      detail.tax_amount_2 = null;
      return {taxAmount1: null, taxAmount2: null, warnings};
    }

    let taxAmount1: number | null = null;
    let taxAmount2: number | null = null;

    if (detail.tax_id_1) {
      const r1 = this.calculateSingleTaxAmount(detail, 'tax_id_1', taxList, taxRates);
      taxAmount1 = r1.amount;
      detail.tax_amount_1 = r1.amount;
      if (r1.warning) {
        warnings.push(r1.warning);
      }
    }

    if (detail.tax_id_2) {
      const r2 = this.calculateSingleTaxAmount(detail, 'tax_id_2', taxList, taxRates);
      taxAmount2 = r2.amount;
      detail.tax_amount_2 = r2.amount;
      if (r2.warning) {
        warnings.push(r2.warning);
      }
    }

    return {taxAmount1, taxAmount2, warnings};
  }
}
